import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { sortTableRows, TableSortOrder } from '../../../utils/table-sort.util';

export interface TableAction {
  label: string;
  icon: string;
  action: string;
  class?: string;
  disabled?: boolean;
  divider?: boolean; // Para adicionar divisores no menu
  visible?: boolean; // Se true, aparece como botão visível; se false, vai para dropdown
}

export interface TableColumn {
  titulo: string;
  label: string;
  formatter?: (value: any) => string;
  customClass?: (value: any, row: any) => string; // Função para retornar classe CSS customizada
  /** Quando true, exibe **** com olho para revelar o valor (uso em apresentações). */
  sensitive?: boolean;
  // Futuro: poderemos adicionar aqui flags específicas de ordenação (ex: sortable?: boolean)
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css']
})
export class TableComponent implements OnInit, OnChanges {
  @Input() title: string = '';
  @Input() data: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() showSearch: boolean = true;
  @Input() searchRight: boolean = false; // Quando true, posiciona o campo de busca à direita
  @Input() searchPlaceholder: string = 'Pesquisar...';
  @Input() clickable: boolean = false;
  @Input() actions: TableAction[] = [];
  @Input() useDropdownMenu: boolean = false; // Forçar uso de dropdown menu
  @Input() visibleActions: TableAction[] = []; // Ações visíveis (fora do dropdown)
  @Input() disablePagination: boolean = false; // Desabilita paginação interna (útil quando backend já faz paginação)
  @Input() totalItemsFromBackend: number = 0; // Total de itens quando backend faz paginação
  @Input() hasNextPageFromBackend: boolean | null = null; // Indica se há próxima página no backend (null = deduzir)
  @Input() hasPreviousPageFromBackend: boolean | null = null; // Indica se há página anterior no backend (null = deduzir)
  @Input() currentPageFromBackend: number = 1; // Página atual quando backend faz paginação
  @Input() totalPagesFromBackend: number = 0; // Total de páginas retornado pela API
  @Input() searchTerm: string = ''; // Permite passar o valor inicial do campo de busca
  @Input() loading: boolean = false; // Exibe overlay de carregamento enquanto os dados são buscados
  // Ordenação: backend emite sortChange; clientSideSort ordena o [data] no próprio componente
  @Input() enableSorting: boolean = false;
  @Input() clientSideSort: boolean = false;
  @Input() sortableColumns: string[] = []; // labels das colunas que podem ordenar; vazio = todas
  @Output() sortChange = new EventEmitter<{ sortBy: string; sortOrder: 'asc' | 'desc' }>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() actionClick = new EventEmitter<{action: string, item: any}>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  filteredData: any[] = [];
  private searchDebounceTimeout: any;

  currentPage: number = 1;
  @Input() itemsPerPage: number = 10;
  itemsPerPageOptions: number[] = [5, 10, 25, 50, 100];
  paginatedData: any[] = [];
  totalItems: number = 0;
  totalPages: number = 0;

  currentSortBy: string | null = null;
  currentSortOrder: TableSortOrder | null = null;

  /** Colunas sensíveis reveladas (olho no cabeçalho): chave = column.label. */
  private revealedSensitiveColumns = new Set<string>();

  private pendingScrollTop: number | null = null;
  private pendingScroller: HTMLElement | Window | null = null;

  // Controle do menu dropdown
  openMenuId: any = null;
  hoverDisabledRowId: any = null;

  // Propriedades para redimensionamento de colunas
  isResizing: boolean = false;
  resizingColumnIndex: number = -1;
  startX: number = 0;
  startWidth: number = 0;
  table: HTMLTableElement | null = null;
  currentResizeHandle: HTMLElement | null = null;

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.filterData();
    this.updatePagination();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['data'] ||
      changes['itemsPerPage'] ||
      changes['totalItemsFromBackend'] ||
      changes['totalPagesFromBackend']
    ) {
      this.filterData();
      this.updatePagination();
    }
    if (changes['currentPageFromBackend']) {
      const newPage = Number(changes['currentPageFromBackend'].currentValue) || 1;
      if (newPage > 0 && newPage !== this.currentPage) {
        this.currentPage = newPage;
        this.updatePagination();
      }
    }
    // Sincronizar searchTerm quando for passado como input (apenas se realmente mudou)
    if (changes['searchTerm'] && !changes['searchTerm'].firstChange) {
      const newValue = changes['searchTerm'].currentValue || '';
      // Só atualiza se o valor realmente mudou (evita loop)
      if (newValue !== this.searchTerm) {
        this.searchTerm = newValue;
      }
    }

    const loadingFinished = changes['loading']
      && !changes['loading'].firstChange
      && changes['loading'].previousValue === true
      && !this.loading;
    const dataArrived = changes['data'] && !changes['data'].firstChange && !this.loading;
    if (loadingFinished || dataArrived) {
      this.restoreScrollPosition();
    }
  }

  onSearchChange(): void {
    // Filtragem client-side (se necessário)
    this.filterData();
    this.currentPage = 1;
    this.updatePagination();
    
    // Debounce antes de emitir o evento (para busca no backend)
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
    
    this.searchDebounceTimeout = setTimeout(() => {
      this.searchChange.emit(this.searchTerm);
      this.searchDebounceTimeout = null;
    }, 600);
  }

  clearSearch(): void {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    this.onSearchChange();
  }

  private getDataForDisplay(): any[] {
    const base = this.data ?? [];
    if (this.clientSideSort && this.enableSorting && this.currentSortBy && this.currentSortOrder) {
      return sortTableRows(base, this.currentSortBy, this.currentSortOrder);
    }
    return base;
  }

  filterData(): void {
    const source = this.getDataForDisplay();

    if (this.totalItemsFromBackend > 0) {
      this.filteredData = [...source];
      return;
    }

    if (!this.searchTerm.trim()) {
      this.filteredData = [...source];
    } else {
      this.filteredData = source.filter(item => {
        return this.columns.some(column => {
          const value = item[column.label];
          if (value !== null && value !== undefined) {
            return value.toString().toLowerCase().includes(this.searchTerm.toLowerCase());
          }
          return false;
        });
      });
    }
  }

  updatePagination() {
    // Se houver totalItemsFromBackend, usar ele (backend faz paginação)
    if (this.totalItemsFromBackend > 0) {
      this.totalItems = this.totalItemsFromBackend;
      this.totalPages =
        this.totalPagesFromBackend > 0
          ? this.totalPagesFromBackend
          : Math.max(1, Math.ceil(this.totalItems / Math.max(1, this.itemsPerPage)));
      this.paginatedData = this.filteredData;
    } else {
      // Paginação local (frontend)
      this.totalItems = this.filteredData.length;
      this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
      
      // Se paginação estiver desabilitada, usar todos os dados filtrados
      if (this.disablePagination) {
        this.paginatedData = this.filteredData;
        return;
      }
      
      // Garantir que a página atual seja válida
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages || 1;
      }
      
      // Calcular dados paginados
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      this.paginatedData = this.filteredData.slice(startIndex, endIndex);
    }
  }

  // ===== ORDENACAO (BACKEND) =====

  /**
   * Verifica se a coluna pode ser usada para ordenação.
   */
  isColumnSortable(column: TableColumn): boolean {
    if (!this.enableSorting) {
      return false;
    }
    if (!this.sortableColumns || this.sortableColumns.length === 0) {
      return true;
    }
    return this.sortableColumns.includes(column.label);
  }

  onSort(column: TableColumn): void {
    if (!this.isColumnSortable(column)) {
      return;
    }

    const label = column.label;

    if (this.currentSortBy === label) {
      this.currentSortOrder = this.currentSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortBy = label;
      this.currentSortOrder = 'asc';
    }

    if (!this.currentSortBy || !this.currentSortOrder) {
      return;
    }

    if (this.clientSideSort) {
      this.filterData();
      this.updatePagination();
      return;
    }

    this.sortChange.emit({
      sortBy: this.currentSortBy,
      sortOrder: this.currentSortOrder
    });
  }

  /**
   * Retorna true se a coluna estiver atualmente ordenada em ordem ascendente.
   */
  isSortedAsc(column: TableColumn): boolean {
    return this.currentSortBy === column.label && this.currentSortOrder === 'asc';
  }

  /**
   * Retorna true se a coluna estiver atualmente ordenada em ordem descendente.
   */
  isSortedDesc(column: TableColumn): boolean {
    return this.currentSortBy === column.label && this.currentSortOrder === 'desc';
  }

  onItemsPerPageChange() {
    this.captureScrollPosition();
    this.currentPage = 1;
    this.updatePagination();
    this.pageSizeChange.emit(this.itemsPerPage);
    this.scheduleScrollRestore();
  }

  goToPage(page: number) {
    this.captureScrollPosition();
    if (this.totalItemsFromBackend > 0) {
      const maxPage = Math.max(1, this.totalPages);
      if (page >= 1 && page <= maxPage) {
        this.currentPage = page;
        this.updatePagination();
        this.pageChange.emit(page);
        this.scheduleScrollRestore();
      }
    } else {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.updatePagination();
        this.pageChange.emit(page);
        this.restoreScrollPosition();
      }
    }
  }

  goToPreviousPage() {
    if (this.totalItemsFromBackend > 0) {
      const canGoPrevious = this.hasPreviousPageFromBackend !== null
        ? this.hasPreviousPageFromBackend
        : this.currentPage > 1;
      if (canGoPrevious) {
        this.goToPage(this.currentPage - 1);
      }
    } else {
      // Paginação local
      if (this.currentPage > 1) {
        this.goToPage(this.currentPage - 1);
      }
    }
  }

  goToNextPage() {
    // Quando backend faz paginação, usar hasNextPageFromBackend
    if (this.totalItemsFromBackend > 0) {
      const canGoNext = this.hasNextPageFromBackend !== null
        ? this.hasNextPageFromBackend
        : this.currentPage < this.totalPages;
      if (canGoNext) {
        this.goToPage(this.currentPage + 1);
      }
    } else {
      // Paginação local
      if (this.currentPage < this.totalPages) {
        this.goToPage(this.currentPage + 1);
      }
    }
  }
  
  // Método público para atualizar página externamente (útil para sincronização com backend)
  setPage(page: number) {
    if (page >= 1) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  private captureScrollPosition(): void {
    const scroller = this.findScrollParent();
    this.pendingScroller = scroller;
    this.pendingScrollTop = scroller instanceof Window
      ? (window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0)
      : scroller.scrollTop;
  }

  private restoreScrollPosition(): void {
    if (this.pendingScrollTop == null) return;
    const top = this.pendingScrollTop;
    const scroller = this.pendingScroller;
    this.pendingScrollTop = null;
    this.pendingScroller = null;
    const apply = () => {
      if (!scroller || scroller instanceof Window) {
        window.scrollTo(0, top);
        document.documentElement.scrollTop = top;
        document.body.scrollTop = top;
      } else {
        scroller.scrollTop = top;
      }
    };
    requestAnimationFrame(apply);
    setTimeout(apply, 50);
  }

  private scheduleScrollRestore(): void {
    const tryRestore = (attempt: number) => {
      if (this.pendingScrollTop == null) return;
      if (!this.loading || attempt >= 10) {
        this.restoreScrollPosition();
        return;
      }
      setTimeout(() => tryRestore(attempt + 1), 80);
    };
    setTimeout(() => tryRestore(0), 0);
  }

  private findScrollParent(): HTMLElement | Window {
    let el: HTMLElement | null = this.elementRef.nativeElement.parentElement;
    while (el) {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 8) {
        return el;
      }
      el = el.parentElement;
    }
    return window;
  }
  
  // Método público para atualizar itemsPerPage externamente
  setItemsPerPage(size: number) {
    if (size > 0) {
      this.itemsPerPage = size;
      this.currentPage = 1;
      this.updatePagination();
    }
  }

  get startItem(): number {
    // Quando backend faz paginação, calcular baseado nos dados recebidos
    if (this.totalItemsFromBackend > 0) {
      const countOnPage = (this.filteredData || []).length;
      if (countOnPage === 0) {
        return 0;
      }
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    }
    // Paginação local
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endItem(): number {
    // Com paginação no servidor, o intervalo "de-até" deve refletir as linhas realmente
    // recebidas (filteredData.length). Caso contrário, totalItems do backend pode ser maior
    // que o tamanho do array (API incompleta) e o rodapé mente (ex.: "1 a 4" com 2 linhas).
    if (this.totalItemsFromBackend > 0) {
      const countOnPage = (this.filteredData || []).length;
      if (countOnPage === 0) {
        return 0;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      return Math.min(start + countOnPage - 1, this.totalItemsFromBackend);
    }
    // Paginação local
    const end = this.currentPage * this.itemsPerPage;
    return Math.min(end, this.totalItems);
  }

  get isFirstPage(): boolean {
    // Quando backend faz paginação, usar hasPreviousPageFromBackend
    if (this.totalItemsFromBackend > 0) {
      if (this.hasPreviousPageFromBackend !== null) {
        return !this.hasPreviousPageFromBackend;
      }
      return this.currentPage <= 1;
    }
    return this.currentPage === 1;
  }

  get isLastPage(): boolean {
    // Quando backend faz paginação, usar hasNextPageFromBackend
    if (this.totalItemsFromBackend > 0) {
      if (this.hasNextPageFromBackend !== null) {
        return !this.hasNextPageFromBackend;
      }
      return this.currentPage >= this.totalPages;
    }
    // Paginação local
    return this.currentPage >= this.totalPages;
  }

  /**
   * Calcula a largura da coluna de ações baseada na quantidade de ações
   */
  get actionsColumnWidth(): string {
    const totalActions = (this.actions?.length || 0) + (this.visibleActions?.length || 0);
    if (totalActions === 0) {
      return '0px';
    }
    
    // Largura base: 24px por botão + 2px de gap + 16px de padding
    const buttonWidth = 24;
    const gap = 2;
    const padding = 16;
    const totalWidth = (totalActions * buttonWidth) + ((totalActions - 1) * gap) + padding;
    
    return `${Math.max(totalWidth, 80)}px`; // Mínimo de 80px
  }

  getActionsColumnWidth(): string {
    const visibleCountFromConfig = this.visibleActionsList.length;
    const hasDropdownFromConfig = this.dropdownActionsList.length > 0;

    // Quando as acoes sao definidas por linha em `_actions` (ex.: Staff),
    // precisamos considerar esse layout para nao colapsar o cabecalho "Ações".
    let visibleCountFromRows = 0;
    let hasDropdownFromRows = false;

    (this.paginatedData || []).forEach((item: any) => {
      if (!item?._actions || !Array.isArray(item._actions)) {
        return;
      }

      const rowVisible = item._actions.filter((a: TableAction) => a.visible === true && !a.divider).length;
      const rowDropdown = item._actions.filter((a: TableAction) => a.visible === false && !a.divider).length;

      if (rowVisible > visibleCountFromRows) {
        visibleCountFromRows = rowVisible;
      }
      if (rowDropdown > 0) {
        hasDropdownFromRows = true;
      }
    });

    const visibleCount = Math.max(visibleCountFromConfig, visibleCountFromRows);
    const hasDropdown = hasDropdownFromConfig || hasDropdownFromRows;
    
    if (visibleCount === 0 && !hasDropdown) {
      return '0px';
    }
    
    // Largura para botões visíveis + botão de menu (se houver)
    const buttonWidth = 36;
    const gap = 8;
    const padding = 16;
    const menuButtonWidth = hasDropdown ? buttonWidth + gap : 0;
    const totalWidth = (visibleCount * buttonWidth) + ((visibleCount > 0 ? visibleCount - 1 : 0) * gap) + menuButtonWidth + padding;
    
    return `${Math.max(totalWidth, 110)}px`;
  }

  onRowClick(item: any): void {
    if (this.clickable) {
      this.rowClick.emit(item);
    }
  }

  onActionClick(action: string, item: any): void {
    this.actionClick.emit({ action, item });
  }

  private normalizeText(value: string | undefined | null): string {
    return (value || '').toLowerCase().trim();
  }

  private isViewAction(action: TableAction): boolean {
    const actionKey = this.normalizeText(action.action);
    const label = this.normalizeText(action.label);
    return actionKey === 'view' || actionKey === 'ver' || label.includes('visualizar') || label === 'ver';
  }

  private isEditAction(action: TableAction): boolean {
    const actionKey = this.normalizeText(action.action);
    const label = this.normalizeText(action.label);
    return actionKey === 'edit' || actionKey === 'editar' || label.includes('editar');
  }

  private isDeleteAction(action: TableAction): boolean {
    const actionKey = this.normalizeText(action.action);
    const label = this.normalizeText(action.label);
    return actionKey === 'delete' || actionKey === 'excluir' || label.includes('excluir') || label.includes('delet');
  }

  getActionClasses(action: TableAction): string[] {
    const classList = new Set<string>();
    if (action.class) {
      action.class.split(' ').filter(Boolean).forEach(cls => classList.add(cls));
    }

    if (this.isViewAction(action)) {
      classList.add('btn-view');
      classList.delete('btn-info');
    } else if (this.isEditAction(action)) {
      classList.add('btn-edit');
      classList.delete('btn-warning');
      classList.delete('btn-primary');
    } else if (this.isDeleteAction(action)) {
      classList.add('btn-delete');
      classList.delete('btn-danger');
    }

    return Array.from(classList);
  }

  getActionIcon(action: TableAction): string {
    if (this.isViewAction(action)) {
      return 'fas fa-eye';
    }
    if (this.isEditAction(action)) {
      return 'fas fa-pen-to-square';
    }
    if (this.isDeleteAction(action)) {
      return 'fas fa-trash-can';
    }
    return action.icon || 'fas fa-circle';
  }

  getDropdownItemClasses(action: TableAction): string[] {
    const semanticClasses = this.getActionClasses(action);
    return [...semanticClasses, action.divider ? 'dropdown-divider' : 'dropdown-item'];
  }

  toggleMenu(itemId: any, event?: MouseEvent): void {
    // Prevenir propagação para evitar que o rowClick seja disparado
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Se já está aberto, fechar; caso contrário, abrir
    this.openMenuId = this.openMenuId === itemId ? null : itemId;
  }

  closeMenu(): void {
    this.openMenuId = null;
  }

  onActionsCellMouseEnter(itemId: any): void {
    this.hoverDisabledRowId = itemId;
  }

  onActionsCellMouseLeave(): void {
    this.hoverDisabledRowId = null;
  }

  getItemId(item: any): any {
    // Tenta obter ID do item (pode ser id, _id, etc)
    return item?.id || item?._id || JSON.stringify(item);
  }

  get shouldUseDropdown(): boolean {
    return this.useDropdownMenu || (this.actions && this.actions.some(a => a.visible === false));
  }

  get visibleActionsList(): TableAction[] {
    if (this.visibleActions && this.visibleActions.length > 0) {
      return this.visibleActions;
    }
    return this.actions?.filter(a => a.visible !== false && !a.divider) || [];
  }

  get dropdownActionsList(): TableAction[] {
    if (this.useDropdownMenu && this.actions) {
      return this.actions.filter(a => a.visible !== true && !a.divider);
    }
    return this.actions?.filter(a => a.visible === false && !a.divider) || [];
  }

  getDropdownActionsForItem(item: any): TableAction[] {
    if (item?._actions && Array.isArray(item._actions)) {
      return item._actions.filter((a: TableAction) => a.visible === false && !a.divider);
    }
    return this.dropdownActionsList;
  }

  /**
   * Botões sempre visíveis na linha.
   * Se a linha tem `_actions` só com `visible: false` (menu ⋮), usa `visibleActions` do componente pai
   * (ex.: Visualizar). Ações com `visible: true` dentro de `_actions` continuam podendo sobrescrever por linha.
   */
  getVisibleActionsForItem(item: any): TableAction[] {
    if (item?._actions && Array.isArray(item._actions)) {
      const rowVisible = item._actions.filter((a: TableAction) => a.visible === true && !a.divider);
      if (rowVisible.length > 0) {
        return rowVisible;
      }
      return this.visibleActionsList;
    }
    return this.visibleActionsList;
  }

  hasActionsForItem(item: any): boolean {
    if (item?._actions && Array.isArray(item._actions)) {
      return item._actions.length > 0;
    }
    return (this.actions && this.actions.length > 0) || (this.visibleActions && this.visibleActions.length > 0);
  }

  hasAnyActions(): boolean {
    if (this.actions && this.actions.length > 0) return true;
    if (this.visibleActions && this.visibleActions.length > 0) return true;
    if (this.useDropdownMenu) return true;
    return this.paginatedData?.some(item => item?._actions && item._actions.length > 0) || false;
  }

  /**
   * Define se o menu de ações (3 pontinhos) deve abrir para cima com base
   * na posição da linha na página atual.
   * Exemplo: nas 5 primeiras linhas abre para baixo; das 5 últimas abre para cima.
   */
  shouldOpenMenuUp(rowIndex: number): boolean {
    const totalRows = this.paginatedData?.length || 0;
    if (totalRows === 0) {
      return false;
    }

    // Se a tabela tiver até 5 linhas, sempre abre para baixo
    if (totalRows <= 5) {
      return false;
    }

    // Considera índice da linha (0-based):
    // - linhas 0..4 (1ª a 5ª) -> abre para baixo (false)
    // - últimas 5 linhas -> abre para cima (true)
    const isInTopFive = rowIndex <= 4;
    const isInBottomFive = rowIndex >= totalRows - 5;

    if (isInBottomFive && !isInTopFive) {
      return true;
    }

    return false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Não processar se o menu não estiver aberto
    if (!this.openMenuId) {
      return;
    }
    
    // Fechar menu se clicar fora dele
    const target = event.target as HTMLElement;
    
    // Verificar se o clique foi no container do menu ou no dropdown
    const isClickInsideMenu = target.closest('.actions-menu-container');
    const isClickOnDropdown = target.closest('.actions-dropdown');
    
    // Não fechar se clicou dentro do menu ou no dropdown
    if (!isClickInsideMenu && !isClickOnDropdown) {
      this.closeMenu();
    }
  }

  /**
   * Retorna classe CSS customizada para uma célula
   */
  getCellClass(value: any, row: any, column: TableColumn): string {
    if (column.customClass && typeof column.customClass === 'function') {
      try {
        return column.customClass(value, row);
      } catch (error) {
        return '';
      }
    }
    return '';
  }

  /**
   * Formata valor para exibição, aplicando formatação de data e valores monetários
   */
  formatDisplayValue(value: any, column: TableColumn): string {
    // Se a coluna tem um formatter customizado, usar ele primeiro
    if (column.formatter && typeof column.formatter === 'function') {
      try {
        return column.formatter(value);
      } catch (error) {
        // Fallback para valor não formatado
      }
    }
    
    // Trata apenas valores realmente nulos ou undefined
    if (value === null || value === undefined) {
      return '-';
    }
    
    // Detecta campos monetários pelo nome da coluna
    const isMoneyColumn = column.label.toLowerCase().includes('amount') || 
                          column.label.toLowerCase().includes('valor') ||
                          column.label.toLowerCase().includes('price') ||
                          column.label.toLowerCase().includes('preco') ||
                          column.titulo.toLowerCase().includes('valor') ||
                          column.titulo.toLowerCase().includes('preço');
    
    if (isMoneyColumn && typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    }
    
    // Detecta campos de data pelo nome da coluna ou tipo do valor
    const isDateColumn = column.label.toLowerCase().includes('date') || 
                        column.label.toLowerCase().includes('data') ||
                        column.titulo.toLowerCase().includes('data');
    
    if (isDateColumn && value) {
      try {
        const date = new Date(value);

        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          if (year < 1900) {
            return 'Não informado';
          }
          return date.toLocaleDateString('pt-BR');
        }
        // Valor já veio formatado (ex.: toLocaleString pt-BR) ou não é parseável
        // por new Date (ex.: "23/04/2026, 11:49") — exibir o texto, não "Data inválida"
        if (typeof value === 'string' && value.trim() !== '') {
          return value;
        }
        return 'Data inválida';
      } catch (error) {
        if (typeof value === 'string' && value.trim() !== '') {
          return value;
        }
        return 'Data inválida';
      }
    }
    
    // Para valores numéricos, incluindo zero, exibe o valor
    if (typeof value === 'number') {
      return value.toString();
    }
    
    // Para strings vazias, retorna string vazia (não o placeholder)
    if (typeof value === 'string') {
      return value; // Mantém string vazia como está
    }
    
    // Para outros tipos, converte para string
    return value.toString();
  }

  isSensitiveColumnRevealed(column: TableColumn): boolean {
    return !!column.sensitive && this.revealedSensitiveColumns.has(column.label);
  }

  toggleSensitiveColumn(event: Event, column: TableColumn): void {
    event.preventDefault();
    event.stopPropagation();
    if (!column.sensitive) return;
    const key = column.label;
    if (this.revealedSensitiveColumns.has(key)) {
      this.revealedSensitiveColumns.delete(key);
    } else {
      this.revealedSensitiveColumns.add(key);
    }
  }

  // Métodos para redimensionamento de colunas
  startResize(event: MouseEvent, columnIndex: number): void {
    event.preventDefault();
    event.stopPropagation();
    
    this.isResizing = true;
    this.resizingColumnIndex = columnIndex;
    this.startX = event.clientX;
    
    this.table = this.elementRef.nativeElement.querySelector('table');
    if (this.table) {
      const th = this.table.querySelector(`th[data-column="${columnIndex}"]`) as HTMLElement;
      if (th) {
        this.startWidth = th.offsetWidth;
      }
    }
    
    document.body.classList.add('resizing-column');
    
    // Adicionar classe visual ao handle
    this.currentResizeHandle = event.target as HTMLElement;
    if (this.currentResizeHandle) {
      this.currentResizeHandle.classList.add('resizing');
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isResizing || !this.table) return;
    
    const diff = event.clientX - this.startX;
    const newWidth = Math.max(80, this.startWidth + diff); // Largura mínima de 80px
    
    const th = this.table.querySelector(`th[data-column="${this.resizingColumnIndex}"]`) as HTMLElement;
    if (th) {
      th.style.width = `${newWidth}px`;
      th.style.minWidth = `${newWidth}px`;
      th.style.maxWidth = `${newWidth}px`;
    }

    // Aplicar a mesma largura para as células do corpo da tabela
    const tds = this.table.querySelectorAll(`td:nth-child(${this.resizingColumnIndex + 1})`);
    tds.forEach((td: Element) => {
      const tdElement = td as HTMLElement;
      tdElement.style.width = `${newWidth}px`;
      tdElement.style.minWidth = `${newWidth}px`;
      tdElement.style.maxWidth = `${newWidth}px`;
    });
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isResizing) {
      this.isResizing = false;
      this.resizingColumnIndex = -1;
      document.body.classList.remove('resizing-column');
      
      // Remover classe visual do handle
      if (this.currentResizeHandle) {
        this.currentResizeHandle.classList.remove('resizing');
        this.currentResizeHandle = null;
      }
    }
  }
}
